/**
 * Helpers para melhorar precisão do OCR e processar cards de visitante
 */

/**
 * Pré-processa imagem para melhorar OCR
 * Aplica: contraste, brilho, nitidez, binarização
 */
export const preprocessImage = (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Aumentar resolução para melhor OCR
        const scale = 2;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        // Desenhar imagem escalada
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Obter dados de pixels
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Aumentar contraste e converter para escala de cinza
        for (let i = 0; i < data.length; i += 4) {
          // Converter para escala de cinza
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          
          // Aumentar contraste (ajustar threshold)
          const threshold = 140;
          const newValue = gray > threshold ? 255 : 0;
          
          data[i] = newValue;     // R
          data[i + 1] = newValue; // G
          data[i + 2] = newValue; // B
          // Alpha mantém
        }

        ctx.putImageData(imageData, 0, 0);

        // Converter canvas para blob
        canvas.toBlob((blob) => {
          resolve(new File([blob], imageFile.name, { type: 'image/png' }));
        }, 'image/png', 0.95);
      };

      img.onerror = reject;
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Dicionário de correções comuns em OCR de caligrafia
 */
const correcoesComunsOCR = {
  // Números confundidos com letras
  '0': ['O', 'o'],
  '1': ['l', 'I', '|'],
  '5': ['S', 's'],
  '8': ['B'],
  
  // Letras confundidas
  'rn': 'm',
  'vv': 'w',
  'ii': 'u',
  'l3': 'B',
  'l5': 'IS',
  '()': 'O',
};

/**
 * Corrige erros comuns de OCR
 */
export const corrigirErrosOCR = (texto) => {
  let corrigido = texto;

  // Aplicar correções de caracteres confusos
  Object.entries(correcoesComunsOCR).forEach(([errado, certos]) => {
    if (Array.isArray(certos)) {
      certos.forEach(c => {
        const regex = new RegExp(c, 'g');
        corrigido = corrigido.replace(regex, errado);
      });
    }
  });

  return corrigido;
};

/**
 * Lista expandida de bairros da Região Metropolitana de Recife/PE
 * Inclui: Recife, Jaboatão, Olinda, Paulista, Cabo, Camaragibe, etc.
 */
const bairrosMetropolitana = [
  // RECIFE (94 bairros oficiais)
  'Aflitos', 'Afogados', 'Água Fria', 'Alto do Mandu', 'Alto José Bonifácio',
  'Alto José do Pinho', 'Alto Santa Terezinha', 'Apipucos', 'Areias',
  'Arruda', 'Barro', 'Beberibe', 'Benfica', 'Boa Viagem', 'Boa Vista',
  'Bongi', 'Brejo da Guabiraba', 'Brejo de Beberibe', 'Cabanga',
  'Caixa d\'Água', 'Campo Grande', 'Casa Amarela', 'Casa Forte', 'Caxangá',
  'Cidade Universitária', 'Coelhos', 'Cohab', 'Cordeiro', 'Coqueiral',
  'Derby', 'Dois Irmãos', 'Dois Unidos', 'Encruzilhada', 'Engenho do Meio',
  'Espinheiro', 'Estância', 'Fundão', 'Graças', 'Guabiraba', 'Hipódromo',
  'Ibura', 'Ilha do Leite', 'Ilha do Retiro', 'Ilha Joana Bezerra',
  'Imbiribeira', 'Iputinga', 'Ipsep', 'Jaqueira', 'Jardim São Paulo',
  'Jiquiá', 'Jordão', 'Linha do Tiro', 'Madalena', 'Mangueira', 'Monteiro',
  'Morro da Conceição', 'Mustardinha', 'Paissandu', 'Parnamirim', 'Passarinho',
  'Pau-Ferro', 'Peixinhos', 'Pina', 'Poço da Panela', 'Ponto de Parada',
  'Porto da Madeira', 'Prado', 'Recife', 'Rosarinho', 'San Martin',
  'Sancho', 'Santana', 'Santo Amaro', 'Santo Antônio', 'São José',
  'Setubal', 'Sitio dos Pintos', 'Sítio Grande', 'Soledade', 'Tamarineira',
  'Tejipió', 'Torre', 'Torrões', 'Torreão', 'Totó', 'Três Carneiros',
  'Torrões', 'Ura', 'Várzea', 'Vasco da Gama', 'Zumbi',
  
  // JABOATÃO DOS GUARARAPES (principais bairros)
  'Barra de Jangada', 'Behque', 'Candeias', 'Cavaleiro', 'Centro',
  'Curado', 'Dois Carneiros', 'Engenho Velho', 'Floriano', 'Guararapes',
  'Jardim Jordão', 'Jardim Piedade', 'Jordão Alto', 'Manoel Júnior',
  'Muribeca', 'Piedade', 'Prazeres', 'Primeira Linha', 'Socorro',
  'Sucupira', 'Vila Rica', 'Zumbi do Pacheco',
  
  // OLINDA (principais bairros)
  'Águas Compridas', 'Alto da Bondade', 'Alto da Conquista', 'Alto da Sé',
  'Alto do Sol Nascente', 'Amaro Branco', 'Bairro Novo', 'Bonsucesso',
  'Bultrins', 'Caixa d\'Água', 'Campestre', 'Carmo', 'Casa Caiada',
  'Cidade Tabajara', 'Coqueirinho', 'Dickson', 'Fragoso', 'Guadalupe',
  'Jardim Atlântico', 'Jardim Brasil', 'Madragoa', 'Monte', 'Ouro Preto',
  'Passarinho', 'Peixinhos', 'Rio Doce', 'Santa Tereza', 'Sítio Novo',
  'Socorro', 'Tabajara', 'Varadouro', 'Vila Popular',
  
  // PAULISTA (principais bairros)
  'Alto do Céu', 'Artur Lundgren I', 'Artur Lundgren II', 'Aurora',
  'Conceição', 'Engenho Maranguape', 'Fragoso', 'Jardim Paulista Alto',
  'Jardim Paulista Baixo', 'Janga', 'Maranguape I', 'Maranguape II',
  'Maria Farinha', 'Mirueira', 'Nobre', 'Nossa Senhora do Ó', 'Paratibe',
  'Pau Amarelo', 'Torres Galvão',
  
  // CABO DE SANTO AGOSTINHO
  'Centro', 'Charneca', 'Cohab', 'Destilaria', 'Gaibú', 'Jardim Atlântico',
  'Monte Castelo', 'Paiva', 'Ponte dos Carvalhos', 'Pontezinha',
  'São Francisco', 'Vila Claudete',
  
  // CAMARAGIBE
  'Aldeia', 'Bairro da Paz', 'Bairro dos Professores', 'Bom Pastor',
  'Borborema', 'Centro', 'Jardim Bela Vista', 'Santa Mônica', 'Tabatinga',
  'Timbi', 'Vale do Sol',
  
  // SÃO LOURENÇO DA MATA
  'Centro', 'Jardim São Paulo', 'Matinha', 'Pixete', 'Tiúma',
  
  // Variações comuns de escrita
  'Jardim Alto', 'Jardins Alto', 'Jd. Alto', 'B. Boa Viagem',
  'B. Casa Amarela', 'B. da Piedade', 'B. da Sagrada',
  'Alto Santo', 'Alto do Santo', 'Jd. Atlântico', 'Jd. Brasil'
];

// Alias: manter compatibilidade com código existente
const bairrosRecife = bairrosMetropolitana;

/**
 * Calcula distância de Levenshtein (similaridade entre strings)
 */
const levenshteinDistance = (str1, str2) => {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
};

/**
 * Corrige nome de bairro usando lista conhecida
 */
export const corrigirBairro = (bairroOCR) => {
  if (!bairroOCR) return { corrigido: '', confianca: 0 };

  const bairroLimpo = bairroOCR.trim().toLowerCase();
  let melhorMatch = { nome: bairroOCR, distancia: 999, confianca: 0 };

  bairrosRecife.forEach(bairro => {
    const distancia = levenshteinDistance(bairroLimpo, bairro.toLowerCase());
    if (distancia < melhorMatch.distancia) {
      melhorMatch = { nome: bairro, distancia, confianca: 0 };
    }
  });

  // Calcular confiança (0-100%)
  const maxDistancia = Math.max(bairroLimpo.length, melhorMatch.nome.length);
  melhorMatch.confianca = Math.max(0, Math.round((1 - melhorMatch.distancia / maxDistancia) * 100));

  // Se confiança muito baixa, retornar original
  if (melhorMatch.confianca < 60) {
    return { corrigido: bairroOCR, confianca: melhorMatch.confianca, sugestao: melhorMatch.nome };
  }

  return { corrigido: melhorMatch.nome, confianca: melhorMatch.confianca };
};

/**
 * Valida e formata CEP
 */
export const validarCEP = (cep) => {
  const cepLimpo = cep.replace(/\D/g, '');
  
  if (cepLimpo.length !== 8) {
    return { valido: false, formatado: cep, confianca: 0 };
  }

  const formatado = `${cepLimpo.substr(0, 5)}-${cepLimpo.substr(5)}`;
  return { valido: true, formatado, confianca: 100 };
};

/**
 * Busca endereço por CEP (API ViaCEP - gratuita)
 */
export const buscarEnderecoPorCEP = async (cep) => {
  const cepLimpo = cep.replace(/\D/g, '');
  
  if (cepLimpo.length !== 8) {
    return null;
  }

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    const data = await response.json();

    if (data.erro) {
      return null;
    }

    return {
      rua: data.logradouro || '',
      bairro: data.bairro || '',
      cidade: data.localidade || '',
      estado: data.uf || '',
      confianca: 100
    };
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
};

/**
 * Limpa e valida telefone brasileiro
 */
export const validarTelefone = (telefone) => {
  const telLimpo = telefone.replace(/\D/g, '');
  
  // Telefone deve ter 10 ou 11 dígitos
  if (telLimpo.length < 10 || telLimpo.length > 11) {
    return { valido: false, formatado: telefone, confianca: 30 };
  }

  // Formatar (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
  let formatado;
  if (telLimpo.length === 11) {
    formatado = `(${telLimpo.substr(0, 2)}) ${telLimpo.substr(2, 5)}-${telLimpo.substr(7)}`;
  } else {
    formatado = `(${telLimpo.substr(0, 2)}) ${telLimpo.substr(2, 4)}-${telLimpo.substr(6)}`;
  }

  return { valido: true, formatado, confianca: 95 };
};

/**
 * Valida email
 */
export const validarEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const valido = regex.test(email);
  
  return {
    valido,
    formatado: email.toLowerCase().trim(),
    confianca: valido ? 90 : 20
  };
};

/**
 * Extrai dados do texto OCR com validação inteligente
 */
export const extrairDadosInteligente = (texto) => {
  const linhas = texto.split('\n').filter(l => l.trim());
  const dados = {
    nome: { valor: '', confianca: 0 },
    telefone: { valor: '', confianca: 0 },
    whatsapp: { valor: '', confianca: 0 },
    email: { valor: '', confianca: 0 },
    cep: { valor: '', confianca: 0 },
    rua: { valor: '', confianca: 0 },
    bairro: { valor: '', confianca: 0 },
    cidade: { valor: '', confianca: 0 },
    estado: { valor: '', confianca: 0 },
    sexo: { valor: '', confianca: 0 },
    checkboxes: {
      quer_ser_membro: false,
      deseja_oracao: false,
      quer_conhecer_jesus: false
    }
  };

  // Extrair nome (primeira linha não numérica)
  for (let i = 0; i < Math.min(3, linhas.length); i++) {
    const linha = linhas[i].trim();
    if (linha.length > 3 && !linha.match(/^\d/) && !linha.match(/@/)) {
      dados.nome = { valor: linha, confianca: 80 };
      break;
    }
  }

  // Extrair telefones
  const regexTelefone = /(\(?\d{2}\)?\s?)?\d{4,5}[-\s]?\d{4}/g;
  const telefones = texto.match(regexTelefone);
  if (telefones && telefones.length > 0) {
    const tel1 = validarTelefone(telefones[0]);
    dados.telefone = { valor: tel1.formatado, confianca: tel1.confianca };
    
    if (telefones.length > 1) {
      const tel2 = validarTelefone(telefones[1]);
      dados.whatsapp = { valor: tel2.formatado, confianca: tel2.confianca };
    }
  }

  // Extrair email
  const regexEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const emails = texto.match(regexEmail);
  if (emails && emails.length > 0) {
    const emailVal = validarEmail(emails[0]);
    dados.email = { valor: emailVal.formatado, confianca: emailVal.confianca };
  }

  // Extrair e validar CEP
  const regexCep = /\d{5}[-]?\d{3}/g;
  const ceps = texto.match(regexCep);
  if (ceps && ceps.length > 0) {
    const cepVal = validarCEP(ceps[0]);
    dados.cep = { valor: cepVal.formatado, confianca: cepVal.confianca };
  }

  // Detectar bairro (procurar em lista conhecida)
  linhas.forEach(linha => {
    const linhaNormalizada = linha.toLowerCase().trim();
    bairrosRecife.forEach(bairro => {
      if (linhaNormalizada.includes(bairro.toLowerCase())) {
        dados.bairro = { valor: bairro, confianca: 95 };
      }
    });
  });

  // Se não encontrou bairro diretamente, tentar correção
  if (dados.bairro.confianca === 0) {
    linhas.forEach(linha => {
      if (linha.length > 3 && linha.length < 30) {
        const correção = corrigirBairro(linha);
        if (correção.confianca > dados.bairro.confianca) {
          dados.bairro = { 
            valor: correção.corrigido, 
            confianca: correção.confianca,
            sugestao: correção.sugestao
          };
        }
      }
    });
  }

  // Detectar cidade/estado (PE comum)
  const textoLower = texto.toLowerCase();
  if (textoLower.includes('recife') || textoLower.includes('rec if')) {
    dados.cidade = { valor: 'Recife', confianca: 95 };
  }
  if (textoLower.includes('pe') || textoLower.includes('pernambuco')) {
    dados.estado = { valor: 'PE', confianca: 95 };
  }

  // Detectar checkboxes por palavras-chave
  if (textoLower.includes('membro') || textoLower.includes('batismo') || textoLower.includes('batista')) {
    dados.checkboxes.quer_ser_membro = true;
  }
  if (textoLower.includes('oração') || textoLower.includes('oracao') || textoLower.includes('orar') || textoLower.includes('orem')) {
    dados.checkboxes.deseja_oracao = true;
  }
  if (textoLower.includes('jesus') || textoLower.includes('salvação') || textoLower.includes('salvacao') || textoLower.includes('conhecer')) {
    dados.checkboxes.quer_conhecer_jesus = true;
  }

  // Detectar sexo
  if (textoLower.includes('masculino') || textoLower.includes('homem') || textoLower.includes('sr.') || textoLower.includes('m ')) {
    dados.sexo = { valor: 'masculino', confianca: 85 };
  } else if (textoLower.includes('feminino') || textoLower.includes('mulher') || textoLower.includes('sra.') || textoLower.includes('f ')) {
    dados.sexo = { valor: 'feminino', confianca: 85 };
  }

  return dados;
};

/**
 * Retorna cor baseada em confiança
 */
export const getCorConfianca = (confianca) => {
  if (confianca >= 80) return 'success'; // Verde
  if (confianca >= 50) return 'warning'; // Amarelo
  return 'error'; // Vermelho
};
